import { isOnline } from '../../shell/connectivity/connectivityInfra';
import { useSessionStore } from '../../shell/session/store/useSessionStore';
import { persistAppSettingSafe } from './persistAppSetting';
import {
  EXPENSES_LAST_PULL_AT_KEY,
  recordSyncPullTimestamp,
} from './syncTimestamps';
const PULL_DEBOUNCE_MS = 30_000;
const PULL_TIMEOUT_MS = 12_000;

let lastPullAttemptAt = 0;
let pullInFlight: Promise<{ pulled: boolean; error?: string }> | null = null;
let pullGeneration = 0;

async function runExpensesPull(
  branchId: string,
  generation: number
): Promise<{ pulled: boolean; error?: string; settlementsCount?: number; expensesCount?: number }> {
  try {
    // Pull siempre completo (sinceIso = null): el snapshot entero se guarda en
    // RAM — el incremental con p_since quedó fuera post-ADR-017.
    const sinceIso = null;
    const { pullBranchExpensesFullRemote } = await import('../../expenses/infra/expensesPullRemote');
    const payload = await Promise.race([
      pullBranchExpensesFullRemote(branchId, sinceIso),
      new Promise<never>((_, reject) =>
        setTimeout(() => reject(new Error('expenses pull remote timeout')), PULL_TIMEOUT_MS)
      ),
    ]);

    if (generation !== pullGeneration) {
      return { pulled: false };
    }

    const { setWebExpensesSnapshotCache } = await import('../../expenses/infra/webExpensesSnapshotCache');
            setWebExpensesSnapshotCache({
              settlements: payload.settlements,
              expenses: payload.expenses,
            });
            const nowIso = new Date().toISOString();
            recordSyncPullTimestamp(EXPENSES_LAST_PULL_AT_KEY, nowIso);
            return {
              pulled: true,
              settlementsCount: payload.settlements.length,
              expensesCount: payload.expenses.length,
            };
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Error al sincronizar pagos';
    console.warn('[pullExpensesWhenNeeded]', message);
    return { pulled: false, error: message };
  }
}

export async function pullExpensesWhenNeeded(
  branchId: string,
  options?: { force?: boolean; requireFresh?: boolean }
): Promise<{ pulled: boolean; error?: string }> {
  if (!branchId) return { pulled: false };

  const online = await isOnline();
  if (!online) return { pulled: false };

  const now = Date.now();
  if (!options?.force && now - lastPullAttemptAt < PULL_DEBOUNCE_MS) {
    return { pulled: false };
  }

  if (pullInFlight) {
    if (!options?.requireFresh) return pullInFlight;
    // El pull en vuelo pudo fetchear antes del write que dispara este force:
    // esperarlo y arrancar uno fresco. Si ya arrancó otro (post-write), coalesce.
    await pullInFlight.catch(() => undefined);
    if (pullInFlight) return pullInFlight;
  }

  lastPullAttemptAt = now;
  const generation = ++pullGeneration;

  pullInFlight = runExpensesPull(branchId, generation).finally(() => {
    if (pullInFlight) {
      pullInFlight = null;
    }
  });

  return pullInFlight;
}

// ─── E2E test-only endpoint ──────────────────────────────────────────────────
// Exposes a function on window that clears the last-pull timestamp and forces
// a full pull (sinceIso = null). This is used by E2E tests to fetch seeded
// settlements that were created after the last pull.
// Guarded by typeof window check — only runs on web, never on native.
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).__e2eForceExpensesPull = async (): Promise<{
    pulled: boolean;
    error?: string;
    settlementsCount?: number;
    expensesCount?: number;
  }> => {
    const branchId = useSessionStore.getState().currentBranchId;
    if (!branchId) return { pulled: false, error: 'No currentBranchId' };
    // Clear the last-pull timestamp so the next pull does a full fetch.
    // Use epoch instead of empty string — the RPC expects a valid timestamp or null.
    await persistAppSettingSafe(EXPENSES_LAST_PULL_AT_KEY, '1970-01-01T00:00:00.000Z');
    // Reset the in-memory debounce so the pull runs immediately.
    lastPullAttemptAt = 0;
    return pullExpensesWhenNeeded(branchId, { force: true });
  };

  // E2E endpoint to query settlements for an expense directly from the web cache.
  // Used to debug why settlements aren't showing in the detail view.
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  (window as unknown as Record<string, unknown>).__e2eQuerySettlements = async (
    expenseId: string
  ): Promise<{ count: number; rows: unknown[] }> => {
    const { getWebExpensesSnapshotCache } = await import('../../expenses/infra/webExpensesSnapshotCache');
    const cached = getWebExpensesSnapshotCache();
    const rows = cached?.settlements.filter((s) => s.expenseId === expenseId) ?? [];
    return { count: rows.length, rows };
  };

  // E2E endpoint to query ALL settlements from the web cache (for debugging).
  // ADR-017 Fase 4: SQLite local eliminado — leer de remote/cache.
  (window as unknown as Record<string, unknown>).__e2eQueryAllSettlements = async (): Promise<{
    count: number;
    rows: unknown[];
  }> => {
    const { getWebExpensesSnapshotCache } = await import('../../expenses/infra/webExpensesSnapshotCache');
    const cached = getWebExpensesSnapshotCache();
    const rows = cached?.settlements.slice(0, 50) ?? [];
    return { count: rows.length, rows };
  };
}
